import type { TipoItem } from "@noesis/shared";

/**
 * Os 16 passos do tour de primeiro uso, em 6 etapas (ADR 041). Texto e alvo
 * vêm do handoff `design_handoff_grifo_tour`, que é alta fidelidade.
 *
 * Cada passo aponta para um elemento real marcado com `data-tour`. **Ação**
 * avança só quando a pessoa faz o que o passo pede — quem faz é o manipulador
 * da própria tela, chamando `avancarSe`. **Info** tem o "entendi →".
 *
 * Os números que o texto cita (itens conferidos, meta, quanto falta) chegam
 * por `DadosDoTour`, informados pela tela que os conhece. Nenhum é escrito
 * aqui (invariante 4).
 */

export type AlvoDoTour =
  | "criar"
  | "criarBtn"
  | "importarCta"
  | "descrever"
  | "copiar"
  | "colar"
  | "conferir"
  | "importarBtn"
  | "icones"
  | "alts"
  | "volta"
  | "ofensiva"
  | "encerrar"
  | "navRevisar"
  | "revLista"
  | "nav";

export type LadoDoPostit = "right" | "left" | "below" | "belowR";

export type DadosDoTour = {
  itensValidos?: number;
  meta?: number;
  faltam?: number;
  tipo?: TipoItem;
};

export type PassoDoTour = {
  etapa: number;
  nomeDaEtapa: string;
  alvo: AlvoDoTour;
  /** Onde o alvo mora: o "voltar ao tour" leva para cá quando a pessoa saiu. */
  tela: string;
  lado: LadoDoPostit;
  tipo: "acao" | "info";
  /** Deslocamento vertical do post-it, para não cobrir o alvo vizinho. */
  dy?: number;
  ultimo?: boolean;
  /**
   * O que o "faça por mim" toca, em ordem, nos passos de ação. Sem isto, é o
   * próprio alvo. Só existe porque o tour é ensaio: tocar não grava nada.
   */
  reserva?: string[];
  /**
   * O que mais pode ser tocado enquanto o passo está na tela, além do alvo, da
   * `reserva` e do post-it. O resto da página fica travado (ver `useTrava`).
   */
  livre?: string[];
  /** Cada linha vira um `<span>` separado no post-it. */
  texto: (d: DadosDoTour) => string[];
};

export const TOTAL_DE_ETAPAS = 6;

function comoResponder(tipo: TipoItem | undefined): string[] {
  switch (tipo) {
    case "certo_errado":
      return ["Marque certo ou errado e confirme.", "No teclado: C, E e Enter."];
    case "multipla":
      return ["Marque uma alternativa e confirme.", "No teclado: a letra da alternativa e Enter."];
    case "cartao":
      return ["Vire o cartão, diga como foi e confirme."];
    case "cloze":
      return ["Preencha as lacunas e confira."];
    default:
      return ["Responda e confirme."];
  }
}

export const PASSOS: readonly PassoDoTour[] = [
  {
    etapa: 1,
    nomeDaEtapa: "Trilha",
    alvo: "criar",
    tela: "/",
    lado: "right",
    tipo: "acao",
    texto: () => [
      "Tudo no Grifo está dentro de trilhas: a matéria ou a prova que você está estudando.",
      "Comece criando a sua.",
    ],
  },
  {
    etapa: 1,
    nomeDaEtapa: "Trilha",
    alvo: "criarBtn",
    tela: "/trilhas/nova",
    lado: "below",
    tipo: "acao",
    // O botão é o alvo, mas o nome se digita antes: os campos do formulário
    // ficam soltos. O "cancelar" é link e fica travado — ele saía do tour.
    livre: ['form:has([data-tour="criarBtn"]) :is(input, select, textarea, label, summary, button)'],
    texto: () => ["Só o nome é obrigatório. Meta e descanso já vêm prontos. O resto fica em opções avançadas."],
  },
  {
    etapa: 2,
    nomeDaEtapa: "Importar",
    alvo: "importarCta",
    tela: "/",
    lado: "right",
    tipo: "acao",
    texto: () => ["Toda trlha começará vazia", "Você pode colocar questões clicando em 'Importar novas questões'."],
  },
  {
    etapa: 2,
    nomeDaEtapa: "Importar",
    alvo: "descrever",
    tela: "/acervo?aba=importar",
    lado: "right",
    tipo: "acao",
    texto: () => ["No campo de número 1 você pode dizer sobre o que quer que sejam as questões. Toque no campo para prosseguir, não precisa digitar."],
  },
  {
    etapa: 2,
    nomeDaEtapa: "Importar",
    alvo: "copiar",
    tela: "/acervo?aba=importar",
    lado: "right",
    tipo: "acao",
    texto: () => ["O Grifo escreve o pedido. Copie e cole no ChatGPT, no Claude ou no modelo que você usa."],
  },
  {
    etapa: 2,
    nomeDaEtapa: "Importar",
    alvo: "colar",
    tela: "/acervo?aba=importar",
    lado: "right",
    tipo: "acao",
    texto: () => ["Você deve copiar a resposta do modelo e colar nesse campo, é só clicar para colar.", "**Esse é um ensaio, não precisa fazer de verdade por agora.**"],
  },
  {
    etapa: 2,
    nomeDaEtapa: "Importar",
    alvo: "conferir",
    tela: "/acervo?aba=importar",
    lado: "right",
    tipo: "acao",
    texto: () => ["Todo lote precisa ser conferido antes de ser gravado. Clique em conferir."],
  },
  {
    etapa: 2,
    nomeDaEtapa: "Importar",
    alvo: "importarBtn",
    tela: "/acervo?aba=importar",
    lado: "left",
    tipo: "acao",
    texto: ({ itensValidos }) => [
      itensValidos === undefined
        ? "Conferido. Importe e comece a primeira sessão."
        : `${itensValidos} ${itensValidos === 1 ? "item válido" : "itens válidos"}. Importe e comece a primeira sessão.`,
    ],
  },
  {
    etapa: 3,
    nomeDaEtapa: "Na questão",
    alvo: "icones",
    tela: "/estudar",
    lado: "belowR",
    tipo: "info",
    texto: () => [
      "**Marcar:** guarda pra ver depois",
      "**Caderno:** abre suas anotações",
      "**Não entendi:** registra a dúvida, não é ajuda",
      "**Assistente:** pergunta sobre a questão",
    ],
  },
  {
    etapa: 3,
    nomeDaEtapa: "Na questão",
    alvo: "alts",
    tela: "/estudar",
    lado: "right",
    tipo: "acao",
    // A primeira alternativa e o Confirmar. O ensaio só tem múltipla e certo
    // ou errado, os dois que se respondem assim.
    reserva: ['[data-tour="alts"] button', ".sessao__principal"],
    texto: ({ tipo }) => comoResponder(tipo),
  },
  {
    etapa: 3,
    nomeDaEtapa: "Na questão",
    alvo: "volta",
    tela: "/estudar",
    lado: "right",
    tipo: "acao",
    reserva: [".sessao__principal"],
    texto: () => [
      "Certa ou errada, a questão vai voltar: seja amanhã ou depois, e depois em intervalos maiores.",
      "Foi difícil / Foi fácil é opcional e ajusta esse intervalo.",
    ],
  },
  {
    etapa: 4,
    nomeDaEtapa: "Meta e ofensiva",
    alvo: "ofensiva",
    tela: "/estudar",
    lado: "belowR",
    tipo: "info",
    texto: ({ meta }) => [
      meta === undefined ? "A meta é quantas questões por dia você quer fazer." : `A meta é quantas questões por dia você quer fazer. (${meta}).`,
      "Cumprir ela soma um dia seguido aqui. Os dias de descanso não quebram a sua sequência.",
    ],
  },
  {
    etapa: 4,
    nomeDaEtapa: "Meta e ofensiva",
    alvo: "encerrar",
    tela: "/estudar",
    lado: "right",
    tipo: "acao",
    texto: ({ faltam }) => [
      faltam ? `Faltam ${faltam}, mas por hoje pode parar. Volte ao início.` : "Meta cumprida. Volte ao início.",
    ],
  },
  {
    etapa: 5,
    nomeDaEtapa: "Revisar",
    alvo: "navRevisar",
    tela: "/",
    lado: "right",
    tipo: "acao",
    texto: () => ["O que você errou veio parar aqui, em Revisar. Pode clicar para ver."],
  },
  {
    etapa: 5,
    nomeDaEtapa: "Revisar",
    alvo: "revLista",
    tela: "/revisar",
    lado: "below",
    tipo: "info",
    texto: () => ["Cada errada fica aqui até você acertar. As questões que vencem no dia também aparecem aqui para você."],
  },
  {
    etapa: 6,
    nomeDaEtapa: "Barra lateral",
    alvo: "nav",
    tela: "/",
    lado: "right",
    tipo: "info",
    ultimo: true,
    texto: () => [
      "O resto está aqui: Simulado, Caderno, Números.",
      "Em Ajustes › Inteligência artificial, uma chave deixa gerar questões sem sair do app.",
    ],
  },
];

/**
 * O passo seguinte quando `alvo` acontece estando em `atual`, ou `null` se o
 * alvo não está no caminho que falta. Procura do passo atual em diante: quem
 * se adiantou (copiou antes de descrever) pula o que já fez, e um alvo de passo
 * que já passou não volta o tour para trás.
 *
 * Por padrão a procura fica **dentro da mesma etapa**, para um toque solto na
 * lateral (Revisar, no passo 1) não atravessar etapas inteiras. `atravessar`
 * libera as etapas seguintes: é o caso de quem **saiu** do passo atual — conta
 * com questões que foi estudar em vez de importar. Sem isso o tour ficava preso
 * num passo cujo alvo nem estava na tela, e nada que a pessoa fizesse o movia.
 */
export function depoisDoAlvo(atual: number, alvo: AlvoDoTour, atravessar = false): number | null {
  const etapa = PASSOS[atual]?.etapa;
  for (let k = Math.max(atual, 0); k < PASSOS.length && (atravessar || PASSOS[k]!.etapa === etapa); k++) {
    if (PASSOS[k]!.alvo === alvo) return k + 1;
  }
  return null;
}
